const BASE32_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
const BASE32_CHARS_INVERSE: Record<string, bigint> = Object.fromEntries(
  BASE32_CHARS.split('').map((c, i) => [c, BigInt(i)]),
);
const BASE32_BITS_PER_BYTE = 8;
const BASE32_BITS_PER_BYTE_BIG = BigInt(BASE32_BITS_PER_BYTE);
const BASE32_BYTES_PER_CHAR = 5;
const BASE32_BYTES_PER_CHAR_BIG = BigInt(BASE32_BYTES_PER_CHAR);

const extractByteBase32 = (num: bigint, index: number) =>
  Number(
    (num >>
      ((BASE32_BYTES_PER_CHAR_BIG - BigInt(index) - 1n) * BASE32_BITS_PER_BYTE_BIG)) &
      0xffn,
  );

export function base32EncodeBinary(data: Uint8Array, padding: string | null = '=') {
  let output = '';
  let buffer = 0;
  let bufferLength = 0;

  for (let i = 0; i < data.length; i++) {
    buffer = (buffer << BASE32_BITS_PER_BYTE) | data[i];
    bufferLength += BASE32_BITS_PER_BYTE;

    while (bufferLength >= BASE32_BYTES_PER_CHAR) {
      output += BASE32_CHARS[(buffer >> (bufferLength - BASE32_BYTES_PER_CHAR)) & 31];
      bufferLength -= BASE32_BYTES_PER_CHAR;
    }
  }

  if (bufferLength > 0) {
    output += BASE32_CHARS[(buffer << (BASE32_BYTES_PER_CHAR - bufferLength)) & 31];
  }

  if (padding !== null) {
    if (padding.length !== 1 || BASE32_CHARS_INVERSE[padding] !== undefined) {
      throw new Error(`Invalid padding argument '${padding}'`);
    }

    let paddingLength = 0;
    switch (data.length % BASE32_BYTES_PER_CHAR) {
      case 1:
        paddingLength = 6;
        break;
      case 2:
        paddingLength = 4;
        break;
      case 3:
        paddingLength = 3;
        break;
      case 4:
        paddingLength = 1;
        break;
    }

    for (let i = 0; i < paddingLength; i++) {
      output += padding;
    }
  }

  return output;
}

export function base32Encode(data: string, padding: string | null = '=') {
  const encoder = new TextEncoder();
  return base32EncodeBinary(encoder.encode(data), padding);
}

export function base32EncodeBinaryNoPadding(data: Uint8Array) {
  return base32EncodeBinary(data, null);
}

export function base32EncodeNoPadding(data: string) {
  const encoder = new TextEncoder();
  return base32EncodeBinaryNoPadding(encoder.encode(data));
}

export function base32DecodeBinary(data: string, padding = '=') {
  if (padding.length !== 1 || BASE32_CHARS_INVERSE[padding] !== undefined) {
    throw new Error(`Invalid padding argument '${padding}'`);
  }

  const bytes: number[] = [];

  // Count number of characters before padding starts
  let lengthNoPadding = data.length;
  for (let i = 0; i < data.length; i++) {
    if (data[i] === padding) {
      lengthNoPadding = i;
      break;
    }
  }

  // Validate the rest of the padding
  for (let i = lengthNoPadding; i < data.length; i++) {
    if (data[i] !== padding) {
      throw new Error(`Malformed padding character '${data[i]}'`);
    }
  }

  // Validate the length of the padding
  const paddingLength = data.length - lengthNoPadding;
  if (
    data.length % 8 !== 0 ||
    (paddingLength !== 0 &&
      paddingLength !== 1 &&
      paddingLength !== 3 &&
      paddingLength !== 4 &&
      paddingLength !== 6)
  ) {
    throw new Error(`Invalid padding length ${paddingLength}`);
  }

  // Decode the data
  let quanta = 0n;
  for (let i = 0; i < lengthNoPadding; i += BASE32_BITS_PER_BYTE) {
    // Read 40 bits into quanta
    quanta = 0n;
    for (let j = i; j < i + BASE32_BITS_PER_BYTE && j < lengthNoPadding; j++) {
      const base32Value = BASE32_CHARS_INVERSE[data[j]];
      if (base32Value === undefined) {
        throw new Error(`Invalid character '${data[j]}'`);
      }

      quanta = (quanta << BASE32_BYTES_PER_CHAR_BIG) | base32Value;
    }

    // Convert currentByte into bytes
    for (let j = 0; j < BASE32_BYTES_PER_CHAR; j++) {
      bytes.push(extractByteBase32(quanta, j));
    }
  }

  // Handle padding
  if (bytes.length > 0 && paddingLength > 0) {
    quanta = quanta << (BASE32_BYTES_PER_CHAR_BIG * BigInt(paddingLength));

    let leftoverBytes = 0;
    switch (paddingLength) {
      case 1:
        leftoverBytes = 4;
        break;
      case 3:
        leftoverBytes = 3;
        break;
      case 4:
        leftoverBytes = 2;
        break;
      case 6:
        leftoverBytes = 1;
        break;
    }

    for (let i = 0; i < leftoverBytes; i++) {
      bytes[bytes.length - BASE32_BYTES_PER_CHAR + i] = extractByteBase32(quanta, i);
    }
    bytes.splice(bytes.length - BASE32_BYTES_PER_CHAR + leftoverBytes);
  }

  return new Uint8Array(bytes);
}

export function base32Decode(data: string, padding = '=') {
  const decoder = new TextDecoder();
  return decoder.decode(base32DecodeBinary(data, padding));
}
